import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adminApi, SwitchBranchResponse } from '../../services/adminApi';

interface AdminState {
    pricingLoading: boolean;
    invoicesLoading: boolean;
    riderPinsLoading: boolean;
    switchBranchLoading: boolean;
    error: string | null;
}

const initialState: AdminState = {
    pricingLoading: false,
    invoicesLoading: false,
    riderPinsLoading: false,
    switchBranchLoading: false,
    error: null,
};

export const runUpdatePricing = createAsyncThunk(
    'admin/runUpdatePricing',
    async (_, { rejectWithValue }) => {
        try {
            const message = await adminApi.runUpdatePricing();
            return message;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to update pricing');
        }
    }
);

export const runUpdateMonthlyInvoices = createAsyncThunk(
    'admin/runUpdateMonthlyInvoices',
    async (_, { rejectWithValue }) => {
        try {
            const message = await adminApi.runUpdateMonthlyInvoices();
            return message;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to update monthly invoices');
        }
    }
);

export const runUpdateRiderPins = createAsyncThunk(
    'admin/runUpdateRiderPins',
    async (_, { rejectWithValue }) => {
        try {
            const message = await adminApi.runUpdateRiderPins();
            return message;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to update rider PINs');
        }
    }
);

export const switchBranch = createAsyncThunk<SwitchBranchResponse, number>(
    'admin/switchBranch',
    async (branchId: number, { rejectWithValue }) => {
        try {
            const response = await adminApi.switchBranch(branchId);
            return response;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to switch branch');
        }
    }
);

const adminSlice = createSlice({
    name: 'admin',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Update Pricing
        builder
            .addCase(runUpdatePricing.pending, (state) => {
                state.pricingLoading = true;
                state.error = null;
            })
            .addCase(runUpdatePricing.fulfilled, (state) => {
                state.pricingLoading = false;
            })
            .addCase(runUpdatePricing.rejected, (state, action) => {
                state.pricingLoading = false;
                state.error = action.payload as string;
            });

        // Update Monthly Invoices
        builder
            .addCase(runUpdateMonthlyInvoices.pending, (state) => {
                state.invoicesLoading = true;
                state.error = null;
            })
            .addCase(runUpdateMonthlyInvoices.fulfilled, (state) => {
                state.invoicesLoading = false;
            })
            .addCase(runUpdateMonthlyInvoices.rejected, (state, action) => {
                state.invoicesLoading = false;
                state.error = action.payload as string;
            });

        // Update Rider PINs
        builder
            .addCase(runUpdateRiderPins.pending, (state) => {
                state.riderPinsLoading = true;
                state.error = null;
            })
            .addCase(runUpdateRiderPins.fulfilled, (state) => {
                state.riderPinsLoading = false;
            })
            .addCase(runUpdateRiderPins.rejected, (state, action) => {
                state.riderPinsLoading = false;
                state.error = action.payload as string;
            });

        // Switch Branch
        builder
            .addCase(switchBranch.pending, (state) => {
                state.switchBranchLoading = true;
                state.error = null;
            })
            .addCase(switchBranch.fulfilled, (state) => {
                state.switchBranchLoading = false;
            })
            .addCase(switchBranch.rejected, (state, action) => {
                state.switchBranchLoading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearError } = adminSlice.actions;
export default adminSlice.reducer;
